/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 410 lines
 * - Adding: ~10 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_STANDARDIZATION
 * Date:         2025-12-07
 * Modified By:  Cursor
 *
 * Description:
 * Standardize toast usage via centralized notifications helper.
 * Replace destructive delete confirmation (window.confirm) with Modal dialog.
 * Enforce consistent UI messaging style and error sanitization.
 *
 * Related Screens / Modules:
 * - client/src/utils/notifications.ts
 * - client/src/features/inventory/AdminInventoryPage.tsx
 */

// [2026-02-07] style-extraction: moved inline styles to SCSS module
import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Alert, Modal, Button } from "react-bootstrap";
import { notify } from "../../utils/notifications";
import InventoryFilters from "./InventoryFilters";
import InventoryTable from "./InventoryTable";
import ViewItemModal from "./ViewItemModal";
import EditItemModal from "./EditItemModal";
import UserAutocomplete from "./UserAutocomplete";
import { useAdminInventory } from "./useAdminInventory";
import styles from "./Inventory.module.scss";
import {
  InventoryItem,
  InventoryFilters as FilterType,
  listByUser,
} from "../../services/adminInventory";
import { AdminUserLite } from "../../services/adminUsers";
import { downloadCSV } from "../../services/adminUsersService";

const AdminInventoryPage: React.FC = () => {
  const [userId, setUserId] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<AdminUserLite | null>(null);
  const [filters, setFilters] = useState<Omit<FilterType, "userId">>({});
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [sortBy, setSortBy] = useState<string>("updatedAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  // [2025-12-07] ui-standardization: Added delete confirmation modal state
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<InventoryItem | null>(null);

  const { data, loading, error, refetch } = useAdminInventory({
    userId,
    page,
    limit,
    sortBy,
    sortOrder,
    ...filters,
  });

  // Restore last userId from sessionStorage on mount
  useEffect(() => {
    const savedUserId = sessionStorage.getItem("admin.inventory.userId");
    if (savedUserId) {
      setUserId(savedUserId);
      // Optionally fetch user details to populate selectedUser
      // For now, we'll just set the userId and let the inventory load
    }
  }, []);

  // Save userId to sessionStorage when it changes
  useEffect(() => {
    if (userId) {
      sessionStorage.setItem("admin.inventory.userId", userId);
    } else {
      sessionStorage.removeItem("admin.inventory.userId");
    }
  }, [userId]);

  const handleFiltersChange = (newFilters: Omit<FilterType, "userId">) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleView = (item: InventoryItem) => {
    setSelectedItem(item);
    setShowViewModal(true);
  };

  const handleEdit = (item: InventoryItem) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  // [2025-12-07] ui-standardization: Replaced window.confirm with Modal confirmation
  const handleDeleteClick = (item: InventoryItem) => {
    setDeleteConfirmItem(item);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmItem(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmItem) return;

    try {
      const { softDelete } = await import("../../services/adminInventory");
      await softDelete(deleteConfirmItem._id);
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.success("Item deleted successfully");
      setDeleteConfirmItem(null);
      refetch();
    } catch (error) {
      console.error("Delete error:", error);
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(error, "We couldn't delete this item. Please try again");
    }
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedItem(null);
    refetch();
  };

  const handleExportCSV = async () => {
    if (!userId) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(null, "Please select a user first");
      return;
    }

    setExportLoading(true);

    try {
      const allItems: any[] = [];
      const pageSize = 100;
      let page = 1;
      let totalPages = 1;

      // Loop through all pages until finished
      do {
        const { data } = await listByUser({
          userId,
          page,
          limit: pageSize,
          ...filters,
          sortBy,
          sortOrder,
        });
        if (data?.items?.length) allItems.push(...data.items);
        totalPages = data.pages || Math.ceil(data.total / data.limit) || 1;
        page += 1;
      } while (page <= totalPages);

      if (!allItems.length) {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.info("No inventory data found for this user");
        return;
      }

      // Create CSV
      const headers = [
        "ID",
        "Manufacturer",
        "Part Number",
        "Model",
        "Title",
        "Quantity",
        "Condition",
        "Price",
        "Currency",
        "Converted Price",
        "Display Currency",
        "Category",
        "Country",
        "Updated At",
        "Seller ID",
        "Seller Name",
        "Seller Company",
      ];

      const rows = allItems.map((r) => [
        r._id,
        r.manufacturer || "",
        r.partNumber || "",
        r.model || "",
        r.title || "",
        r.quantity ?? "",
        r.condition || "",
        r.price ?? "",
        r.currency || "",
        r.convertedPrice ?? "",
        r.displayCurrency || "",
        r.category || "",
        r.country || "",
        r.updatedAt || "",
        r.sellerId?._id || "",
        [r.sellerId?.firstName, r.sellerId?.lastName].filter(Boolean).join(" "),
        r.sellerId?.companyName || "",
      ]);

      const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

      // Create filename with user name
      let userName = "user";
      if (selectedUser) {
        const fullName = [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join("_");
        if (fullName) {
          userName = fullName;
        } else if (selectedUser.email) {
          userName = selectedUser.email.split("@")[0]; // Use email prefix
        } else if (selectedUser.companyName) {
          userName = selectedUser.companyName;
        } else {
          userName = `user_${userId}`; // Fallback to user ID
        }
      } else {
        userName = `user_${userId}`; // Fallback to user ID if no selectedUser
      }

      const sanitizedUserName = userName.replace(/[^a-zA-Z0-9_-]/g, "_");
      const filename = `inventory_${sanitizedUserName}_${
        new Date().toISOString().split("T")[0]
      }.csv`;

      downloadCSV(blob, filename);
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.success(`Exported ${allItems.length} records to ${filename}`);
    } catch (error) {
      console.error("Export error:", error);
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(error, "We couldn't export the data. Please try again");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Container fluid className={`py-4 ${styles.fullWidthContainer}`}>
      <Row>
        <Col className={styles.flexColumn}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Admin Inventory Management</h4>
              <div className="d-flex gap-2">
                <button
                  className="btn-figma-1 btn-sm"
                  onClick={handleExportCSV}
                  disabled={!userId || loading || exportLoading}
                >
                  Export CSV
                </button>
              </div>
            </Card.Header>
            <Card.Body>
              {/* User Selection */}
              <Row className="mb-4">
                <Col md={6}>
                  <label className="form-label">User</label>
                  <UserAutocomplete
                    value={selectedUser}
                    onChange={(u) => {
                      setSelectedUser(u);
                      if (u) {
                        setUserId(u._id);
                        sessionStorage.setItem("admin.inventory.userId", u._id);
                        setPage(1); // Reset to first page when user changes
                      } else {
                        setUserId("");
                        sessionStorage.removeItem("admin.inventory.userId");
                      }
                    }}
                    placeholder="Search by name or email"
                    autoFocus
                  />
                  {selectedUser && (
                    <div className="mt-2">
                      <span className="badge bg-light text-dark me-2">
                        {`${
                          [selectedUser.firstName, selectedUser.lastName]
                            .filter(Boolean)
                            .join(" ") ||
                          selectedUser.email ||
                          selectedUser.companyName ||
                          selectedUser._id
                        }`}
                      </span>
                      <button
                        type="button"
                        className="btn-figma-1 btn-sm"
                        onClick={() => {
                          setSelectedUser(null);
                          setUserId("");
                          sessionStorage.removeItem("admin.inventory.userId");
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </Col>
              </Row>

              {!userId && (
                <Alert variant="info">
                  Please select a user to start managing their inventory.
                </Alert>
              )}

              {userId && (
                <>
                  {/* Filters */}
                  <InventoryFilters
                    filters={filters}
                    onChange={handleFiltersChange}
                    loading={loading}
                  />

                  {/* Error Display */}
                  {error && (
                    <Alert variant="danger" className="mt-3">
                      Error loading inventory: {error}
                    </Alert>
                  )}

                  {/* Table */}
                  <InventoryTable
                    rows={data?.items || []}
                    total={data?.total || 0}
                    page={page}
                    limit={limit}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    loading={loading}
                    onSort={handleSort}
                    onPageChange={handlePageChange}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modals */}
      <ViewItemModal
        show={showViewModal}
        item={selectedItem}
        onHide={() => {
          setShowViewModal(false);
          setSelectedItem(null);
        }}
      />

      <EditItemModal
        show={showEditModal}
        item={selectedItem}
        onHide={() => {
          setShowEditModal(false);
          setSelectedItem(null);
        }}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Confirmation Modal */}
      {/* [2025-12-07] ui-standardization: Replaced window.confirm with Modal */}
      <Modal show={!!deleteConfirmItem} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Are you sure you want to delete "
            {deleteConfirmItem?.title || deleteConfirmItem?.manufacturer || "this item"}"? This
            action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminInventoryPage;
